"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlComponentRendererFactory = void 0;
const gl_component_renderer_1 = require("./component-renderer/gl-component-renderer");
const rc_js_util_1 = require("rc-js-util");
const gl_compile_error_1 = require("../../errors/gl-compile-error");
const developer_error_localization_1 = require("../../errors/developer-error-localization");
/**
 * @public
 * Webgl component renderer factory.
 */
class GlComponentRendererFactory {
    constructor(context, localizations, sharedState, extensions) {
        this.context = context;
        this.localizations = localizations;
        this.sharedState = sharedState;
        this.extensions = extensions;
    }
    static getExtensions(context, requiredExtensions) {
        if (context == null) {
            return null;
        }
        const isGl2 = context instanceof WebGL2RenderingContext;
        const extensions = {};
        for (let i = 0, iEnd = requiredExtensions.length; i < iEnd; ++i) {
            const name = requiredExtensions[i];
            const ext = context.getExtension(name);
            if (ext == null) {
                if (isGl2 && GlComponentRendererFactory.gl2StandardExtensions.has(name)) {
                    continue;
                }
                throw rc_js_util_1._Production.createError(`extension "${name}" is required but not available`);
            }
            extensions[name] = ext;
        }
        return extensions;
    }
    static createOne(context, extensions, sharedState, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    localizations = developer_error_localization_1.developerErrorLocalization) {
        if (context == null || extensions == null || sharedState == null) {
            return null;
        }
        return new GlComponentRendererFactory(context, localizations, sharedState, extensions);
    }
    createRenderer(specification) {
        DEBUG_MODE && rc_js_util_1._Debug.assert(!this.context.isContextLost(), "tried to compile program during context loss");
        const program = this.compileProgram(specification.vertexShader.source, specification.fragmentShader.source, specification.outAttributes);
        const optionalExts = rc_js_util_1._Array.collect(specification.optionalExtensions, {}, (extensions, name) => {
            const ext = this.context.getExtension(name);
            if (ext != null) {
                extensions[name] = ext;
            }
        });
        return new gl_component_renderer_1.GlComponentRenderer(this.context, program, Object.assign(Object.assign({}, this.extensions), optionalExts), specification, this.sharedState);
    }
    compileProgram(vertexShaderSource, fragmentShaderSource, outAttributes) {
        const vertexShader = this.compileShader(this.context.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = this.compileShader(this.context.FRAGMENT_SHADER, fragmentShaderSource);
        const program = this.context.createProgram();
        if (program == null) {
            throw rc_js_util_1._Production.createError("failed to create program");
        }
        this.context.attachShader(program, vertexShader);
        this.context.attachShader(program, fragmentShader);
        if (rc_js_util_1._Array.isNotEmpty(outAttributes) && this.context instanceof WebGL2RenderingContext) {
            // FIXME this should support interleaving as an option
            this.context.transformFeedbackVaryings(program, outAttributes, this.context.SEPARATE_ATTRIBS);
        }
        this.context.linkProgram(program);
        if (this.context.getProgramParameter(program, this.context.LINK_STATUS)) {
            return program;
        }
        else {
            const causedBy = this.context.getProgramInfoLog(program);
            this.context.deleteProgram(program);
            throw new gl_compile_error_1.GlCompileError(this.localizations.glCompileError, causedBy, this.localizations.getTx);
        }
    }
    setContext(context) {
        this.context = context;
    }
    compileShader(type, source) {
        const shader = this.context.createShader(type);
        if (shader == null) {
            throw rc_js_util_1._Production.createError(`failed to create shader of type ${type}`);
        }
        this.context.shaderSource(shader, source);
        this.context.compileShader(shader);
        const compiled = this.context.getShaderParameter(shader, this.context.COMPILE_STATUS);
        DEBUG_MODE && rc_js_util_1._Debug.conditionalBlock("DEBUG_SHADER_COMPILATION", () => {
            const ext = this.context.getExtension("WEBGL_debug_shaders");
            if (ext != null) {
                console.debug(ext.getTranslatedShaderSource(shader));
            }
        });
        if (!compiled) {
            const causedBy = this.context.getShaderInfoLog(shader);
            this.context.deleteShader(shader);
            throw new gl_compile_error_1.GlCompileError(this.localizations.glCompileError, causedBy, this.localizations.getTx);
        }
        else {
            return shader;
        }
    }
}
exports.GlComponentRendererFactory = GlComponentRendererFactory;
GlComponentRendererFactory.gl2StandardExtensions = new Set([
    "ANGLE_instanced_arrays",
    "OES_vertex_array_object",
    "WEBGL_depth_texture",
    "OES_texture_float",
    "OES_texture_float_linear",
    "OES_texture_half_float",
    "OES_texture_half_float_linear",
    "OES_standard_derivatives",
    "OES_element_index_uint",
    "EXT_frag_depth",
    "EXT_blend_minmax",
    "EXT_shader_texture_lod",
    "WEBGL_draw_buffers",
    "EXT_sRGB",
]);
//# sourceMappingURL=gl-component-renderer-factory.js.map